import { Component, inject, signal } from '@angular/core';
import { NgForm } from '@angular/forms';
import { AppRoutes } from 'src/app/core/models/routes.model';
import { BaseComponent } from 'src/app/core/components/base-component/base-component.component';
import { EcommerceAuthService } from 'src/app/core/services/ecommerce/ecommerce-auth.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { extractApiErrorDetail } from 'src/app/core/utils/http/api-error.util';
import { ApiError } from 'src/app/core/models/errors/api-error.model';

type ResetPasswordState = 'form' | 'done' | 'missing-token';

const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 128;

/**
 * Recupero password, passo 2: rotta pubblica `/reset-password?token=...`,
 * destinazione del link inviato da Odoo. `POST /auth/password-reset/confirm`
 * imposta la nuova password; poi si prosegue con il login Keycloak, che dal
 * 15/09/2026 è l'accesso dell'ecommerce e usa la stessa password.
 */
@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: false,
})
export class ResetPasswordComponent extends BaseComponent {
  private readonly ecommerceAuthService = inject(EcommerceAuthService);
  private readonly authService = inject(AuthService);

  readonly AppRoutes = AppRoutes;
  readonly passwordMinLength = PASSWORD_MIN_LENGTH;
  readonly passwordMaxLength = PASSWORD_MAX_LENGTH;

  readonly state = signal<ResetPasswordState>('form');
  /** Testo restituito dal backend a conferma, preferito al nostro default. */
  readonly message = signal<string>('');

  model = { password: '', confirmPassword: '' };
  isSubmitting = false;
  submitError: string | null = null;
  /** Token non valido o scaduto: si offre il link per richiederne uno nuovo. */
  canRequestNewLink = false;

  private token = '';

  override ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token')?.trim() ?? '';
    if (!this.token) {
      this.state.set('missing-token');
    }
    super.ngOnInit();
  }

  get passwordsDoNotMatch(): boolean {
    return (
      this.model.confirmPassword.length > 0 && this.model.password !== this.model.confirmPassword
    );
  }

  async onSubmit(form: NgForm): Promise<void> {
    if (form.invalid || this.passwordsDoNotMatch || this.isSubmitting) {
      form.control.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.submitError = null;
    this.canRequestNewLink = false;
    try {
      const response = await this.ecommerceAuthService.confirmPasswordReset(
        this.token,
        this.model.password
      );
      this.model = { password: '', confirmPassword: '' };
      this.message.set(response.detail || 'La tua password è stata aggiornata.');
      this.state.set('done');
    } catch (error) {
      const fallback =
        'Non è stato possibile aggiornare la password. Il link potrebbe essere scaduto: richiedine uno nuovo.';
      // Su QA (15/09/2026) un token non valido produce un 500 con messaggio
      // "Internal Server Error": non è presentabile, si usa il fallback.
      const isServerError = error instanceof ApiError && error.originalStatus >= 500;
      this.submitError = isServerError ? fallback : extractApiErrorDetail(error, fallback);
      // Il token è monouso e a scadenza: in caso di errore la via d'uscita più
      // probabile è una nuova richiesta, quindi si mostra sempre il link.
      this.canRequestNewLink = true;
    } finally {
      this.isSubmitting = false;
    }
  }

  /** Accesso Keycloak con la nuova password: al ritorno si atterra sugli acquisti. */
  async onKeycloakLogin(): Promise<void> {
    await this.authService.login(
      `/${AppRoutes.MAIN_ROUTE}/${AppRoutes.APP}/${AppRoutes.DEFAULT_APP_CODE}/${AppRoutes.ACQUISTI}`
    );
  }

  goToPlans(): void {
    void this.router.navigateByUrl(
      `/${AppRoutes.MAIN_ROUTE}/${AppRoutes.APP}/${AppRoutes.DEFAULT_APP_CODE}/${AppRoutes.DASHBOARD}`
    );
  }
}
